import { useState, useEffect } from 'react';
import { authService } from '../services/api';
import { AuthContext } from './authCtx';
import { getShellToken, setShellToken, clearShellToken, sanitizeSharedStorageForShell } from '../utils/shellToken';
import {
  maybeRedirectEntryHost,
  rememberEntryHost,
  redirectToDoorLogin,
  platformDoorHost,
  clearRememberedEntryHost,
  clearRememberedTrainingBrand,
} from '../utils/entryHost';
import {
  startIdleSessionWatcher,
  touchSessionActivity,
  clearAllModuleSessions,
  clearSessionActivity,
} from '../utils/idleSession';
import { clearAuthTenantBrand, resetDocumentFavicon } from '../utils/documentBrand';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getShellToken());

  // Verificar si hay una sesión activa
  useEffect(() => {
    const validateToken = async () => {
      try {
        sanitizeSharedStorageForShell();
        // Escape a Fit Live: Nest logout o link ?shell_logout=1 limpia sesión shell.
        let forceShellLogout = false;
        try {
          forceShellLogout = new URLSearchParams(window.location.search).get('shell_logout') === '1';
        } catch { /* noop */ }
        if (forceShellLogout) {
          clearRememberedEntryHost();
          clearRememberedTrainingBrand();
          clearAllModuleSessions();
          clearShellToken();
          clearSessionActivity();
          clearAuthTenantBrand();
          resetDocumentFavicon();
          try {
            sessionStorage.removeItem('futbol_sso_sent');
            sessionStorage.removeItem('futbol_auto_sso');
            sessionStorage.removeItem('entry_host_redirecting');
          } catch { /* noop */ }
          setToken(null);
          setUser(null);
          try {
            const u = new URL(window.location.href);
            u.searchParams.delete('shell_logout');
            window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
          } catch { /* noop */ }
          return;
        }
        const activeToken = getShellToken();
        if (activeToken && activeToken !== token) {
          setToken(activeToken);
          return;
        }
        if (token) {
          const response = await authService.getProfile();
          setUser(response.data);
          rememberEntryHost(response.data);
          touchSessionActivity();
          // Sin futbol_auto_sso: no expulsar a Nest al abrir Fit Live.
          maybeRedirectEntryHost(response.data).catch(() => {});
        } else {
          setUser(null);
        }
      } catch (error) {
        // 401/403 en perfil = JWT shell inválido o sesión corrupta (p. ej. token FOOD en localStorage).
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          clearShellToken();
          setToken(null);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Cierre automático tras 1h de inactividad (shell + módulos).
  // No vigilar mientras loading: el SSO Training (`entry_sso`) no debe
  // chocar con un `d28d_last_activity_at` viejo y mandar a /?reason=idle.
  useEffect(() => {
    if (!token || loading) return undefined;
    return startIdleSessionWatcher({
      isActive: () => Boolean(getShellToken()),
      onIdle: () => {
        setToken(null);
        setUser(null);
      },
    });
  }, [token, loading]);

  const login = async (email, password) => {
    const response = await authService.login(email, password);
    const { token: newToken, user: loginUser } = response.data;
    // Evitar montar Dashboard con el payload de login (sin plan/licencias
    // hidratadas): el boot one-shot mandaba Food-only al Inicio D28D.
    touchSessionActivity();
    setLoading(true);
    setShellToken(newToken);
    setToken(newToken);
    try { sessionStorage.setItem('futbol_auto_sso', '1'); } catch { /* noop */ }
    try {
      const profile = await authService.getProfile();
      setUser(profile.data);
      rememberEntryHost(profile.data);
      await maybeRedirectEntryHost(profile.data);
    } catch {
      setUser(loginUser);
      rememberEntryHost(loginUser);
      await maybeRedirectEntryHost(loginUser);
    } finally {
      setLoading(false);
      touchSessionActivity();
    }
    return response.data;
  };

  const loginWithEntrySso = async (ticket) => {
    const response = await authService.consumeEntrySso(ticket);
    const { token: newToken, user: loginUser } = response.data;
    touchSessionActivity();
    setLoading(true);
    setShellToken(newToken);
    setToken(newToken);
    try { sessionStorage.setItem('futbol_auto_sso', '1'); } catch { /* noop */ }
    try {
      const profile = await authService.getProfile();
      setUser(profile.data);
      rememberEntryHost(profile.data);
      await maybeRedirectEntryHost(profile.data);
    } catch {
      setUser(loginUser);
      rememberEntryHost(loginUser);
      await maybeRedirectEntryHost(loginUser);
    } finally {
      setLoading(false);
      touchSessionActivity();
    }
    return response.data;
  };

  const register = async (userData) => {
    const response = await authService.register(userData);
    return response.data;
  };

  const logout = () => {
    let host = platformDoorHost(user) || 'd28d';
    // academia.app: al salir del shell volver a Fit Live, nunca al Nest /access.
    if (host === 'futbol' || host === 'd28d') {
      host = 'd28d';
      clearRememberedEntryHost();
      clearRememberedTrainingBrand();
    }
    try {
      sessionStorage.removeItem('futbol_sso_sent');
      sessionStorage.removeItem('futbol_auto_sso');
      sessionStorage.removeItem('entry_host_redirecting');
    } catch { /* noop */ }
    clearAllModuleSessions();
    clearShellToken();
    clearSessionActivity();
    clearAuthTenantBrand();
    resetDocumentFavicon();
    setToken(null);
    setUser(null);
    redirectToDoorLogin({ host });
  };

  const refreshProfile = async () => {
    if (!token) return null;
    const response = await authService.getProfile();
    setUser(response.data);
    rememberEntryHost(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithEntrySso, register, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
